import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Grip, LifeBuoy, LogOut, ShieldCheck, Ticket, X } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { helpdeskUrl } from '@/lib/helpdesk';
import { cn } from '@/lib/utils';
import { useNadcNavigation, type NadcAppLink } from '@/app/hooks/useNadcNavigation';

/**
 * The NADC apps button (2026-10-09).
 *
 * Michael dropped the shared NADC bar from DemoOps to save screen space — "I
 * am thinking about dropping the common top nav bar to save even more screen
 * size". What the bar's user menu offered lives here instead, in DemoOps' own
 * header beside the bell: who you are signed in as, the NADC apps you may
 * open (the portal's own list, in its launcher's order and colours, DemoOps
 * marked as where you are), My Tickets, Get Help, Admin Tools for portal
 * admins, and Sign out — the side menu's own sign-out, so there is one path.
 * The NADC logo went with the bar.
 *
 * Built like the bell (NotificationBell): a popover under the button on a
 * desk; `compact` (the phone header) pins the same panel full-width under the
 * header with a Close, because a phone has no "outside" to tap. Escape and an
 * outside click close it. Nine dots (Grip), not four squares (LayoutGrid):
 * those are the phone's Workspaces button, a few pixels away.
 */

// navbar.js's APP_CATALOG, copied so the grid reads the same here as in every
// other NADC app: launcher order and tile colour, keyed by host. It grants
// nothing — only apps in the portal's answer are drawn.
const APP_CATALOG: Record<string, { order: number; color: string }> = {
  'nadc1.net': { order: 0, color: '#f59e0b' }, // Portal
  'contacts.nadc1.net': { order: 1, color: '#00d4aa' }, // Directory
  'calendar.nadc1.net': { order: 2, color: '#e05545' }, // Calendar
  'files.nadc1.net': { order: 3, color: '#73c41d' }, // Files
  'scraptrack.nadc1.net': { order: 4, color: '#10b981' }, // ScrapTrack
  'projectmanagement.nadc1.net': { order: 5, color: '#10b981' }, // DemoOps
  'equipment.nadc1.net': { order: 6, color: '#e05545' }, // Equipment
  'mileage.nadc1.net': { order: 7, color: '#00d4aa' }, // Mileage
  'bidlog.nadc1.net': { order: 8, color: '#73c41d' }, // BidLog
  'helpdesk.nadc1.net': { order: 9, color: '#f7c51e' }, // Help Desk
  'ai.nadc1.net': { order: 10, color: '#e05545' }, // AI Assistant
};

// DemoOps' own host. Matched as well as the page's host so DemoOps stays the
// current app on preview.nadc1.net and in development.
const DEMOOPS_HOST = 'projectmanagement.nadc1.net';
const HELPDESK_HOST = 'helpdesk.nadc1.net';

// navbar.js's stable accent for an app the catalogue does not know yet.
const ACCENTS = ['#f7c51e', '#3788d8', '#10b981', '#9b59b6', '#f59e0b', '#00d4aa', '#e05545', '#73c41d'];

function tileColour(app: NadcAppLink): string {
  const known = APP_CATALOG[app.host];
  if (known) return known.color;
  let h = 0;
  for (let i = 0; i < app.name.length; i++) h = (h * 31 + app.name.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
}

/** Catalogue order; apps it does not know go last, in the portal's order (sort is stable). */
function orderedApps(links: NadcAppLink[]): NadcAppLink[] {
  return [...links].sort((a, b) => (APP_CATALOG[a.host]?.order ?? 900) - (APP_CATALOG[b.host]?.order ?? 900));
}

function isCurrentApp(app: NadcAppLink): boolean {
  return app.host === DEMOOPS_HOST || app.host === window.location.host.replace(/^www\./, '');
}

const ROW =
  'flex min-h-touch w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-graphite-200 transition-colors hover:bg-graphite-800 hover:text-graphite-100 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-safety-500';

export function NadcAppsLauncher({ compact = false }: { compact?: boolean }) {
  // Called with the shell, not on open: the answer is in before the menu is.
  const nav = useNadcNavigation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // As the bell: focus that was inside the panel goes back to the button.
  // Escape and Close always send it there — a click in Safari leaves focus on
  // <body>, and whoever pressed Escape is a keyboard user who wants it back.
  const close = useCallback((refocus: boolean) => {
    if (refocus || panelRef.current?.contains(document.activeElement)) buttonRef.current?.focus();
    setOpen(false);
  }, []);

  // Escape and outside-click close. Mousedown rather than click so a drag
  // that starts inside and ends outside does not dismiss it.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close(true);
    }
    function onPointer(e: MouseEvent | TouchEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open, close]);

  // The portal's name for the person when it answered, else DemoOps' own.
  const name = nav.user?.name ?? user?.name ?? null;
  const department = nav.user?.department ?? null;
  const hasHelpdesk = nav.links.some((l) => l.host === HELPDESK_HOST);

  return (
    <div ref={rootRef} className={cn('relative', compact && 'static')}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? close(false) : setOpen(true))}
        aria-label="NADC apps"
        title="NADC apps"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={cn(
          'rounded-md text-graphite-400 transition-colors hover:bg-graphite-800 hover:text-graphite-100',
          // The desk's, as the bell's: 36px in the top row, 44 on a touch screen.
          compact ? 'flex h-9 w-9 items-center justify-center' : 'flex h-9 w-9 items-center justify-center [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11',
          open && 'bg-graphite-800 text-graphite-100',
        )}
      >
        <Grip className="h-5 w-5" aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="NADC apps"
          className={cn(
            // Over the Guide (z-[70]), as the bell's: the Guide leaves the top
            // row usable, so what the row opens draws over its panel or sheet.
            'z-[75] flex flex-col overflow-hidden rounded-lg border border-graphite-700 bg-graphite-900 shadow-xl',
            compact
              ? 'absolute left-2 right-2 top-full mt-1 max-h-[75vh]'
              : 'absolute right-0 top-full mt-2 w-80 max-h-[70vh]',
          )}
        >
          <div className="flex items-start justify-between gap-2 border-b border-graphite-800 px-4 py-3">
            <div className="min-w-0">
              {name && <p className="truncate text-sm font-semibold text-graphite-100">{name}</p>}
              {department && <p className="truncate text-xs text-graphite-400">{department}</p>}
            </div>
            {compact && (
              <button
                type="button"
                onClick={() => close(true)}
                aria-label="Close"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>

          <div className="overflow-y-auto">
            <ul aria-label="Apps" className="grid grid-cols-2 gap-1 p-2">
              {orderedApps(nav.links).map((app, i) => {
                const current = isCurrentApp(app);
                return (
                  <li key={`${i}:${app.url}`}>
                    {/* Same tab, as the portal's launcher: these are the NADC apps, not outside sites. */}
                    <a
                      href={app.url}
                      aria-current={current ? 'true' : undefined}
                      className={cn(
                        'flex min-h-[48px] items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-graphite-800 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-safety-500',
                        current ? 'bg-graphite-800 font-semibold text-safety-500' : 'text-graphite-200 hover:text-graphite-100',
                      )}
                    >
                      <span
                        data-testid="nadc-app-tile"
                        aria-hidden="true"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-graphite-950"
                        style={{ backgroundColor: tileColour(app) }}
                      >
                        {app.name.charAt(0).toUpperCase()}
                      </span>
                      <span data-testid="nadc-app-name" className="min-w-0 break-words leading-tight">{app.name}</span>
                    </a>
                  </li>
                );
              })}
            </ul>

            <ul className="border-t border-graphite-800 p-2">
              {/* The portal shows My Tickets only to people with the help desk among their apps. */}
              {hasHelpdesk && (
                <li>
                  <a href="https://nadc1.net/my-tickets" className={ROW}>
                    <Ticket className="h-4 w-4 shrink-0 text-graphite-400" aria-hidden="true" />
                    <span>{nav.openTickets > 0 ? `My Tickets (${nav.openTickets} open)` : 'My Tickets'}</span>
                  </a>
                </li>
              )}
              <li>
                <a href={helpdeskUrl()} className={ROW}>
                  <LifeBuoy className="h-4 w-4 shrink-0 text-graphite-400" aria-hidden="true" />
                  <span>Get Help</span>
                </a>
              </li>
              {nav.user?.isPortalAdmin && (
                <li>
                  <a href="https://nadc1.net/admin/" className={ROW}>
                    <ShieldCheck className="h-4 w-4 shrink-0 text-graphite-400" aria-hidden="true" />
                    <span>Admin Tools</span>
                  </a>
                </li>
              )}
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void logout();
                  }}
                  className={ROW}
                >
                  <LogOut className="h-4 w-4 shrink-0 text-graphite-400" aria-hidden="true" />
                  <span>Sign out</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
